import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Popconfirm, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	requestForProposalStatusChangeForBuyer,
	requestForProposalStatusChangeForSupplier,
	requestsForProposalRestCrudThunksForBuyer,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';
import { Link } from 'react-router-dom';
import { requestForProposalStatusIndicatorFormatter } from '../../utils/DataFormatterUtils';
import moment from 'moment';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import {
	compareStrings,
	disabledByAnotherAction,
	getEntityById,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import DateTimeHover from '../date_time_component/DateTime';
import {
	biddersRestCrudThunksForBuyer,
	biddersRestCrudThunksForSupplier,
} from '../../thunks/bidders_thunks';

const actionButtons = {
	publish: 'Publish',
	cancel: 'Cancel',
	close: 'Close',
	reopen: 'Re-Open',
};

const RFPDetailPage: FC<any> = ({
	getRequestForProposal,
	match,
	history,
	deleteRequestForProposal,
	requestForProposal,
	publishRequestForProposal,
	closeRequestForProposal,
	requestsForProposalFetching,
	childRoutes,
	companyConfig,
	userType,
	getBidders,
}): React.ReactElement => {
	const [actionButtonLoading, setActionButtonLoading] = useState();
	const requestForProposalId = useMemo(() => parseInt(match.params.id, 10), [match.params.id]);

	const [popConfirmVisible, setPopConfirmVisible] = useState(false);

	const popConfirmToggle = useCallback(() => setPopConfirmVisible((val) => !val), []);

	useEffect(() => {
		setPopConfirmVisible(true);
	}, []);

	useEffect(() => {
		getRequestForProposal(requestForProposalId);
		getBidders({ requestForProposalId }, 'RFP_DETAIL_BIDDERS');
	}, [requestForProposalId]);

	const handleTabChange = useCallback(
		(key) => {
			const requestForProposalId = parseInt(match.params.id, 10);
			history.push(`/${userType}/requestsForProposal/detail/${requestForProposalId}/${key}`);
		},
		[history, match.params.id, userType]
	);

	const handlereOpen = useCallback(() => {
		setActionButtonLoading(actionButtons.reopen);
		publishRequestForProposal(requestForProposal).then(() => {
			message.success('RFP has been re-opened.');
			setActionButtonLoading(null);
		});
	}, [deleteRequestForProposal, history, requestForProposal, userType, setActionButtonLoading]);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1 || (key === 'compareBids' && pathname.endsWith('/bid'));
		},
		[history.location.pathname]
	);

	const publishEvent = useCallback(() => {
		setActionButtonLoading(actionButtons.publish);
		publishRequestForProposal(requestForProposal).then(() => {
			message.success('RFP has been published.');
			setActionButtonLoading(null);
		});
	}, [publishRequestForProposal, requestForProposal, setActionButtonLoading]);

	const endEvent = useCallback(() => {
		setActionButtonLoading(actionButtons.close);
		closeRequestForProposal(requestForProposal)
			.then(() => {
				setActionButtonLoading(null);
				message.success('RFP has ended.');
			})
			.catch(message.error);
	}, [closeRequestForProposal, requestForProposal, setActionButtonLoading]);

	const tabs = useMemo(
		() => [
			{
				key: 'details',
				name: 'Overview',
			},
			{
				key: 'compareBids',
				name: 'Bids',
			},

			{
				key: 'notes',
				name: 'Notes',
			},
		],
		[]
	);

	const changeBiddingStatusButton = useMemo(() => {
		switch (requestForProposal.status) {
			case 'draft':
				return (
					<Popconfirm
						onConfirm={publishEvent}
						placement="topLeft"
						title="Publish event and notify bidders?"
						okText="Publish event"
						cancelText="Cancel"
						okButtonProps={{
							loading: actionButtonLoading === actionButtons.publish,
							disabled: disabledByAnotherAction(actionButtonLoading, actionButtons.publish),
						}}
						cancelButtonProps={{
							loading: actionButtonLoading === actionButtons.cancel,
							disabled: disabledByAnotherAction(actionButtonLoading, actionButtons.cancel),
						}}
						onCancel={popConfirmToggle}
						visible={popConfirmVisible}
					>
						<Button ghost={true} onClick={popConfirmToggle}>
							Publish RFP
						</Button>
					</Popconfirm>
				);
			case 'active':
				return (
					<Popconfirm
						onConfirm={endEvent}
						placement="topLeft"
						title="Close event and notify bidders?"
						okText="Close event"
						okButtonProps={{
							loading: actionButtonLoading === actionButtons.close,
							disabled: disabledByAnotherAction(actionButtonLoading, actionButtons.close),
						}}
						cancelButtonProps={{
							loading: actionButtonLoading === actionButtons.cancel,
							disabled: disabledByAnotherAction(actionButtonLoading, actionButtons.cancel),
						}}
						cancelText="Cancel"
					>
						<Button ghost={true} loading={actionButtonLoading === actionButtons.close}>
							End RFP
						</Button>
					</Popconfirm>
				);
			case 'closed':
				return null;
			default:
				return null;
		}
	}, [
		endEvent,
		popConfirmToggle,
		popConfirmVisible,
		publishEvent,
		requestForProposal.status,
		actionButtonLoading,
	]);

	return (
		<div className="rfpDetailPage">
			<DetailPageHeader
				headerClassName="rfp-detail-header"
				exactPath={`/${userType}/requestsForProposal/detail/:id`}
				redirectPath={`/${userType}/requestsForProposal/detail/${requestForProposalId}/details`}
				backLinkText={'Back to RFPs'}
				backLinkTo={`/${userType}/requestsForProposal`}
				title={requestsForProposalFetching ? null : requestForProposal.title}
				subtitle={
					<div className="rfpDetailPage__subtitleRow">
						<div className="rfpDetailPage__subtitle">
							{requestForProposalStatusIndicatorFormatter(requestForProposal.status)}
						</div>
						<div className="rfpDetailPage__subtitle">
							{moment().diff(requestForProposal.dueDate, 'h') < 0 ? (
								<div>
									Responses due{' '}
									<DateTimeHover
										timestamp={nullSafeGet('dueDate', requestForProposal)}
										showYear={false}
									/>
								</div>
							) : (
								<div>
									Bidding closed{' '}
									<DateTimeHover
										timestamp={nullSafeGet('dueDate', requestForProposal)}
										showYear={false}
									/>
								</div>
							)}
						</div>
					</div>
				}
				actions={[
					changeBiddingStatusButton,
					<Link
						to={`/${userType}/requestsForProposal/edit/${requestForProposalId}`}
						style={{ marginLeft: 16, marginRight: 16 }}
					>
						<Button ghost={true}>Edit</Button>
					</Link>,
					compareStrings(requestForProposal.status, 'closed') ? (
						<Button
							ghost={true}
							onClick={handlereOpen}
							loading={actionButtonLoading === actionButtons.reopen}
							disabled={disabledByAnotherAction(actionButtonLoading, actionButtons.reopen)}
						>
							Re-Open
						</Button>
					) : null,
				]}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				tabs={tabs}
				handleTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	requestsForProposalFetching: state.requests_for_proposal.fetching,
	requestForProposal: getEntityById(ownProps.match.params.id, state.requests_for_proposal),
	companyConfig: state.company_config,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateRequestForProposal: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.update(entity)
				: requestsForProposalRestCrudThunksForBuyer.update(entity)
		),
	getRequestForProposal: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.readOne(id)
				: requestsForProposalRestCrudThunksForBuyer.readOne(id)
		),
	deleteRequestForProposal: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: requestsForProposalRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
	publishRequestForProposal: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestForProposalStatusChangeForSupplier('active', entity)
				: requestForProposalStatusChangeForBuyer('active', entity)
		),
	closeRequestForProposal: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestForProposalStatusChangeForSupplier('closed', entity)
				: requestForProposalStatusChangeForBuyer('closed', entity)
		),
	getBidders: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? biddersRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
				: biddersRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
		),
});

const RFPDetailConnectedPage = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(RFPDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(RFPDetailConnectedPage);
